class WebAudioSoundManager {
  private audioContext: AudioContext | null = null;
  private enabled: boolean = true;
  private gainNode: GainNode | null = null;

  constructor() {
    this.initAudioContext();
  }

  private initAudioContext() {
    try {
      // Create audio context on first user interaction
      if (typeof window !== 'undefined') {
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        if (AudioContextClass) {
          this.audioContext = new AudioContextClass();
          this.gainNode = this.audioContext.createGain();
          this.gainNode.connect(this.audioContext.destination);
          this.gainNode.gain.value = 0.3;
        }
      }
    } catch (error) {
      console.warn('Web Audio API not supported:', error);
    }
  }

  private ensureAudioContext() {
    if (!this.audioContext) {
      this.initAudioContext();
    }
    // Resume context if it's suspended (happens on mobile)
    if (this.audioContext?.state === 'suspended') {
      this.audioContext.resume();
    }
  }

  play(soundName: string) {
    if (!this.enabled) return;
    
    this.ensureAudioContext();
    if (!this.audioContext || !this.gainNode) return;

    try {
      const now = this.audioContext.currentTime;
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(this.gainNode);

      switch (soundName) {
        case 'click':
          // Short click
          oscillator.frequency.value = 800;
          oscillator.type = 'square';
          gainNode.gain.setValueAtTime(0.1, now);
          gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.05);
          oscillator.start(now);
          oscillator.stop(now + 0.05);
          break;

        case 'correct':
          // Happy ascending notes
          oscillator.type = 'sine';
          oscillator.frequency.setValueAtTime(523, now); // C5
          oscillator.frequency.setValueAtTime(659, now + 0.1); // E5
          oscillator.frequency.setValueAtTime(784, now + 0.2); // G5
          gainNode.gain.setValueAtTime(0.2, now);
          gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.4);
          oscillator.start(now);
          oscillator.stop(now + 0.4);
          
          // Add a second oscillator for harmony
          const osc2 = this.audioContext.createOscillator();
          osc2.type = 'sine';
          osc2.connect(gainNode);
          osc2.frequency.setValueAtTime(262, now); // C4
          osc2.frequency.setValueAtTime(330, now + 0.1); // E4
          osc2.frequency.setValueAtTime(392, now + 0.2); // G4
          osc2.start(now);
          osc2.stop(now + 0.4);
          break;

        case 'wrong':
          // Descending buzz
          oscillator.type = 'sawtooth';
          oscillator.frequency.setValueAtTime(200, now);
          oscillator.frequency.exponentialRampToValueAtTime(100, now + 0.3);
          gainNode.gain.setValueAtTime(0.15, now);
          gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
          oscillator.start(now);
          oscillator.stop(now + 0.3);
          break;

        case 'complete':
          // Victory fanfare
          oscillator.type = 'sine';
          const notes = [523, 659, 784, 1047]; // C5, E5, G5, C6
          let time = now;
          notes.forEach((freq, i) => {
            oscillator.frequency.setValueAtTime(freq, time);
            time += 0.15;
          });
          gainNode.gain.setValueAtTime(0.3, now);
          gainNode.gain.setValueAtTime(0.3, now + 0.5);
          gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.8);
          oscillator.start(now);
          oscillator.stop(now + 0.8);
          break;

        case 'gameOver':
          // Sad descending notes
          oscillator.type = 'triangle';
          oscillator.frequency.setValueAtTime(440, now); // A4
          oscillator.frequency.setValueAtTime(349, now + 0.2); // F4
          oscillator.frequency.setValueAtTime(294, now + 0.4); // D4
          gainNode.gain.setValueAtTime(0.2, now);
          gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.6);
          oscillator.start(now);
          oscillator.stop(now + 0.6);
          break;
      }
    } catch (error) {
      console.warn(`Failed to play sound: ${soundName}`, error);
    }
  }

  setEnabled(enabled: boolean) {
    this.enabled = enabled;
  }

  stopAll() {
    // Web Audio API oscillators stop automatically
  }
}

export const soundManager = new WebAudioSoundManager();